import { useLazyQuery } from '@apollo/client';
import { alertSlice } from '../../../context/AlertContext';
import { authenticationSettingsSlice } from '../../../context/AuthenticationSettingsContext';
import { binStatusSlice } from '../../../context/BinStatusContext';
import { commentSlice } from '../../../context/CommentContext';
import { containerTypeSlice } from '../../../context/ContainerTypeContext';
import { csvUploadConfigurationSlice } from '../../../context/CsvUploadConfigurationContext';
import { customFieldSlice } from '../../../context/CustomFieldContext';
import { fileSlice } from '../../../context/FileContext';
import { orderExportConfigurationSlice } from '../../../context/OrderExportConfigurationContext';
import { reorderRuleSlice } from '../../../context/ReorderRuleContext';
import { reorderUserRuleSlice } from '../../../context/ReorderUserRuleContext';
import { scanRuleSlice } from '../../../context/ScanRuleContext';
import { stockLocationRoleAssignmentSlice } from '../../../context/StockLocationRoleAssignmentContext';
import { tagSlice } from '../../../context/TagContext';
import { tagRelationSlice } from '../../../context/TagRelationContext';
import { taskSlice } from '../../../context/TaskContext';
import { taskExportConfigurationSlice } from '../../../context/TaskExportConfigurationContext';
import { taskSettingsSlice } from '../../../context/TaskSettingsContext';
import { GetAlertResponse, GetAlertVariables, AlertQueries } from '../../../graphql/alert.graphql';
import {
  GetAuthenticationSettingsResponse,
  GetAuthenticationSettingsVariables,
  AuthenticationSettingsQueries,
} from '../../../graphql/authenticationSettings.graphql';
import { GetBinStatusesResponse, GetBinStatusesVariables, BinStatusQueries } from '../../../graphql/binStatus.graphql';
import { GetCommentsResponse, GetCommentsVariables, CommentQueries } from '../../../graphql/comment.graphql';
import {
  GetContainerTypesResponse,
  GetContainerTypesVariables,
  ContainerTypeQueries,
} from '../../../graphql/containerType.graphql';
import {
  GetCsvUploadConfigurationsResponse,
  GetCsvUploadConfigurationsVariables,
  CsvUploadConfigurationQueries,
} from '../../../graphql/csvUploadConfiguration.graphql';
import {
  GetCustomFieldResponse,
  GetCustomFieldVariables,
  CustomFieldQueries,
} from '../../../graphql/customField.graphql';
import { GetFilesResponse, GetFilesVariables, FileQueries } from '../../../graphql/file.graphql';
import {
  GetOrderExportConfigurationsResponse,
  GetOrderExportConfigurationsVariables,
  OrderExportConfigurationQueries,
} from '../../../graphql/orderExportConfiguration.graphql';
import {
  GetProductTransactionsResponse,
  GetProductTransactionsVariables,
  ProductTransactionQueries,
} from '../../../graphql/productTransaction.graphql';
import {
  GetReorderRulesResponse,
  GetReorderRulesVariables,
  ReorderRuleQueries,
} from '../../../graphql/reorderRule.graphql';
import {
  GetReorderUserRulesResponse,
  GetReorderUserRulesVariables,
  ReorderUserRuleQueries,
} from '../../../graphql/reorderUserRule.graphql';
import { GetScanRuleResponse, GetScanRuleVariables, ScanRuleQueries } from '../../../graphql/scanRule.graphql';
import {
  GetStockLocationRoleAssignmentsResponse,
  GetStockLocationRoleAssignmentsVariables,
  StockLocationRoleAssignmentQueries,
} from '../../../graphql/stockLocationRoleAssignment.graphql';
import { GetTagsResponse, GetTagsVariables, TagQueries } from '../../../graphql/tag.graphql';
import {
  GetTagRelationsResponse,
  GetTagRelationsVariables,
  TagRelationQueries,
} from '../../../graphql/tagRelation.graphql';
import { GetTasksResponse, GetTasksVariables, TaskQueries } from '../../../graphql/task.graphql';
import {
  GetTaskSettingsResponse,
  GetTaskSettingsVariables,
  TaskSettingsQueries,
} from '../../../graphql/taskSettings.graphql';
import { GetTriggerResponse, GetTriggerVariables, TriggerQueries } from '../../../graphql/trigger.graphql';
import { orderSlice } from '../../../providers/OrderProvider';
import { productMasterDataSlice } from '../../../providers/ProductMasterDataProvider';
import { productSlice } from '../../../providers/ProductProvider';
import { Company } from '../../../types/company';
import { useAppDispatch } from '../store';
import { triggerSlice } from '../../../providers/TriggerProvider';
import {
  GetIntegrationSettingsResponse,
  GetIntegrationSettingsVariables,
  IntegrationSettingsQueries,
} from '../../../graphql/integrationSettings.graphql';
import { productTransactionSlice } from '../../../providers/ProductTransactionProvider';
import { reorderRuleExportConfigurationSlice } from '../../../providers/ReorderRuleExportConfigurationProvider';
import {
  GetHandlingUnitsResponse,
  GetHandlingUnitsVariables,
  HandlingUnitQueries,
} from '../../../graphql/handlingUnit.graphql';
import { handlingUnitSlice } from '../../../providers/HandlingUnitProvider';
import {
  GetKitTemplatesResponse,
  GetKitTemplatesVariables,
  KitTemplateQueries,
} from '../../../graphql/kitTemplate.graphql';
import { kitTemplateSlice } from '../../../context/KitTemplateContext';
import { kitSlice } from '../../../context/KitContext';
import { GetKitsResponse, GetKitsVariables, KitQueries } from '../../../graphql/kit.graphql';
import { integrationSettingsSlice } from '../../../providers/IntegrationProvider';
import { useRequest } from '../../../requests/common/requestHook';
import { ProductMasterDataRequest } from '../../../requests/productMasterData.request';
import { ToastType } from '../../components/Toast';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import { useToast } from '../../../providers/ToastProvider';
import { translate } from '../../../types/translation/Translator';
import { BinRequest } from '../../../requests/bin.request';
import { ProductTransactionRequest } from '../../../requests/productTransaction.request';
import { OrderRequest } from '../../../requests/order.request';
import { LotRequest } from '../../../requests/lot.request';
import { ContainerRequest } from '../../../requests/container.request';
import { ProductRequest } from '../../../requests/product.request';
import { StockLocationRequest } from '../../../requests/stockLocation.request';
import { binSlice } from '../../../providers/BinProvider';
import { lotSlice } from '../../../providers/LotProvider';
import { containerSlice } from '../../../providers/ContainerProvider';
import { stockLocationSlice } from '../../../providers/StockLocationProvider';
import { ProductExportConfigurationRequest } from '../../../requests/productExportConfiguration.request';
import { productExportConfigurationSlice } from '../../../providers/ProductExportConfigurationProvider';
import { ContactRequest } from '../../../requests/contact.request';
import { contactSlice } from '../../../providers/ContactProvider';
import { ProductMasterDataExportConfigurationRequest } from '../../../requests/productMasterDataExportConfiguration.request';
import { productMasterDataExportConfigurationSlice } from '../../../providers/ProductMasterDataExportConfigurationProvider';
import { BarcodeExportConfigurationRequest } from '../../../requests/barcodeExportConfiguration.request';
import { barcodeExportConfigurationSlice } from '../../../providers/BarcodeExportConfigurationProvider';
import { dashboardSlice } from '../../../providers/DashboardProvider';
import { BinConstraintRequest } from '../../../requests/binConstraint.request';
import { binConstraintSlice } from '../../../providers/BinConstraintProvider';
import { AssetTransactionRequest } from '../../../requests/assetTransaction.request';
import { AssetRequest } from '../../../requests/asset.request';
import { assetTransactionSlice } from '../../../providers/AssetTransactionProvider';
import { assetSlice } from '../../../providers/AssetProvider';
import { pdfTemplateSlice } from '../../../providers/PdfTemplateProvider';
import { PdfTemplateRequest } from '../../../requests/pdf.request';
import { TableSettingsRequest } from '../../../requests/tableSettings.request';
import { tableSettingsSlice } from '../../../providers/TableSettingsProvider';
import { ProductTransactionExportConfigurationRequest } from '../../../requests/productTransactionExportConfiguration.request';
import { productTransactionExportConfigurationSlice } from '../../../providers/ProductTransactionExportConfigurationProvider';
import { AssetExportConfigurationRequest } from '../../../requests/assetExportConfiguration.request';
import { assetExportConfigurationSlice } from '../../../providers/AssetExportConfigurationProvider';
import { TaskExportConfigurationRequest } from '../../../requests/taskExportConfiguration.request';
import { ReorderRuleExportConfigurationRequest } from '../../../requests/reorderRuleExportConfiguration.request';
import { OrderFlowRequest } from '../../../requests/orderFlow.request';
import { orderFlowSlice } from '../../../providers/OrderFlowProvider';
import { OrderStatusRequest } from '../../../requests/orderStatus.request';
import { orderStatusSlice } from '../../../providers/OrderStatusProvider';
import { ProductTransactionStatusRequest } from '../../../requests/productTransactionStatus.request';
import { productTransactionStatusSlice } from '../../../providers/ProductTransactionStatusProvider';
import { ProductStatusRequest } from '../../../requests/productStatus.request';
import { productStatusSlice } from '../../../providers/ProductStatusProvider';

export function useCompanyApi(currentCompany: Company = new Company({ id: '0', companyId: '0' })) {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const [productMasterData] = useRequest(ProductMasterDataRequest.list);
  const fetchProductMasterData = async (page: number = 0) => {
    const response = await productMasterData({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.productMasterDataUnableToLoadError }),
      });
    }

    dispatch(productMasterDataSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchProductMasterData(++page);
    } else {
      dispatch(productMasterDataSlice.actions.setLoading(false));
    }
  };

  const [bins] = useRequest(BinRequest.list);
  const fetchBins = async (page: number = 0) => {
    const response = await bins({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.binsUnableToLoadError }),
      });
    }

    dispatch(binSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchBins(++page);
    } else {
      dispatch(binSlice.actions.setLoading(false));
    }
  };

  const [productTransactions] = useRequest(ProductTransactionRequest.list);
  const fetchProductTransactions = async (page: number = 0) => {
    const response = await productTransactions({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.productTransactionsUnableToLoadError }),
      });
    }

    dispatch(productTransactionSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchProductTransactions(++page);
    } else {
      dispatch(productTransactionSlice.actions.setLoading(false));
    }
  };

  const [orders] = useRequest(OrderRequest.list);
  const fetchOrders = async (page: number = 0) => {
    const response = await orders({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.ordersUnableToLoadError }),
      });
    }

    dispatch(orderSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchOrders(++page);
    } else {
      dispatch(orderSlice.actions.setLoading(false));
    }
  };

  const [lots] = useRequest(LotRequest.list);
  const fetchLots = async (page: number = 0) => {
    const response = await lots({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.lotsUnableToLoadError }),
      });
    }

    dispatch(lotSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchLots(++page);
    } else {
      dispatch(lotSlice.actions.setLoading(false));
    }
  };

  const [containers] = useRequest(ContainerRequest.list);
  const fetchContainers = async (page: number = 0) => {
    const response = await containers({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.containersUnableToLoadError }),
      });
    }

    dispatch(containerSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchContainers(++page);
    } else {
      dispatch(containerSlice.actions.setLoading(false));
    }
  };

  const [products] = useRequest(ProductRequest.list);
  const fetchProducts = async (page: number = 0) => {
    const response = await products({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.productsUnableToLoadError }),
      });
    }

    dispatch(productSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchProducts(++page);
    } else {
      dispatch(productSlice.actions.setLoading(false));
    }
  };

  const [stockLocations] = useRequest(StockLocationRequest.list);
  const fetchStockLocations = async (page: number = 0) => {
    const response = await stockLocations({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.stockLocationsUnableToLoadError }),
      });
    }

    dispatch(stockLocationSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchStockLocations(++page);
    } else {
      dispatch(stockLocationSlice.actions.setLoading(false));
    }
  };

  const [contacts] = useRequest(ContactRequest.list);
  const fetchContacts = async (page: number = 0) => {
    const response = await contacts({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.contactsUnableToLoadError }),
      });
    }

    dispatch(contactSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchContacts(++page);
    } else {
      dispatch(contactSlice.actions.setLoading(false));
    }
  };

  const [productExportConfigurations] = useRequest(ProductExportConfigurationRequest.list);
  const fetchProductExportConfigurations = async (page: number = 0) => {
    const response = await productExportConfigurations({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.productExportConfigurationsUnableToLoadError }),
      });
    }

    dispatch(productExportConfigurationSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchProductExportConfigurations(++page);
    } else {
      dispatch(productExportConfigurationSlice.actions.setLoading(false));
    }
  };

  const [taskExportConfigurations] = useRequest(TaskExportConfigurationRequest.list);
  const fetchTaskExportConfigurations = async (page: number = 0) => {
    const response = await taskExportConfigurations({ page });

    if (!response) {
      return;
    }

    dispatch(taskExportConfigurationSlice.actions.addAll(response.data));
    if (response.hasNext === true) {
      await fetchTaskExportConfigurations(++page);
    } else {
      dispatch(taskExportConfigurationSlice.actions.setLoading(false));
    }
  };

  const [productMasterDataExportConfigurations] = useRequest(ProductMasterDataExportConfigurationRequest.list);
  const fetchProductMasterDataExportConfigurations = async (page: number = 0) => {
    const response = await productMasterDataExportConfigurations({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.productMasterDataExportConfigurationsUnableToLoadError }),
      });
    }

    dispatch(productMasterDataExportConfigurationSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchProductMasterDataExportConfigurations(++page);
    } else {
      dispatch(productMasterDataExportConfigurationSlice.actions.setLoading(false));
    }
  };

  const [producTransationExportConfigurations] = useRequest(ProductTransactionExportConfigurationRequest.list);
  const fetchProductTransactionExportConfigurations = async (page: number = 0) => {
    const response = await producTransationExportConfigurations({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.productTransactionExportConfigurationsUnableToLoadError }),
      });
    }

    dispatch(productTransactionExportConfigurationSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchProductTransactionExportConfigurations(++page);
    } else {
      dispatch(productTransactionExportConfigurationSlice.actions.setLoading(false));
    }
  };

  const [barcodeExportConfigurations] = useRequest(BarcodeExportConfigurationRequest.list);
  const fetchBarcodeExportConfigurations = async (page: number = 0) => {
    const response = await barcodeExportConfigurations({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.barcodeExportConfigurationsUnableToLoadError }),
      });
    }

    dispatch(barcodeExportConfigurationSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchBarcodeExportConfigurations(++page);
    } else {
      dispatch(barcodeExportConfigurationSlice.actions.setLoading(false));
    }
  };

  const [assetExportConfigurations] = useRequest(AssetExportConfigurationRequest.list);
  const fetchAssetExportConfigurations = async (page: number = 0) => {
    const response = await assetExportConfigurations({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.assetExportConfigurationsUnableToLoadError }),
      });
    }

    dispatch(assetExportConfigurationSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchAssetExportConfigurations(++page);
    } else {
      dispatch(assetExportConfigurationSlice.actions.setLoading(false));
    }
  };

  const [reorderRuleExportConfigurations] = useRequest(ReorderRuleExportConfigurationRequest.list);
  const fetchReorderRuleExportConfigurations = async (page: number = 0) => {
    const response = await reorderRuleExportConfigurations({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.reorderRuleExportConfigurationsUnableToLoadError }),
      });
    }

    dispatch(reorderRuleExportConfigurationSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchReorderRuleExportConfigurations(++page);
    } else {
      dispatch(reorderRuleExportConfigurationSlice.actions.setLoading(false));
    }
  };

  const [binConstraints] = useRequest(BinConstraintRequest.list);
  const fetchBinConstraints = async (page: number = 0) => {
    const response = await binConstraints({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.binConstraintsUnableToLoadError }),
      });
    }

    dispatch(binConstraintSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchBinConstraints(++page);
    } else {
      dispatch(binConstraintSlice.actions.setLoading(false));
    }
  };

  const [assetTransactions] = useRequest(AssetTransactionRequest.list);
  const fetchAssetTransactions = async (page: number = 0) => {
    const response = await assetTransactions({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.assetTransactionsUnableToLoadError }),
      });
    }

    dispatch(assetTransactionSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchAssetTransactions(++page);
    } else {
      dispatch(assetTransactionSlice.actions.setLoading(false));
    }
  };

  const [assets] = useRequest(AssetRequest.list);
  const fetchAssets = async (page: number = 0) => {
    const response = await assets({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.assetsUnableToLoadError }),
      });
    }

    dispatch(assetSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchAssets(++page);
    } else {
      dispatch(assetSlice.actions.setLoading(false));
    }
  };

  const [pdfTemplates] = useRequest(PdfTemplateRequest.list);
  const fetchPdfTemplates = async (page: number = 0) => {
    const response = await pdfTemplates({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,

        title: translate({ key: TranslationKey.pdfTemplatesUnableToLoadError }),
      });
    }

    dispatch(pdfTemplateSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchPdfTemplates(++page);
    } else {
      dispatch(pdfTemplateSlice.actions.setLoading(false));
    }
  };

  const [tableSettings] = useRequest(TableSettingsRequest.list);
  const fetchTableSettings = async (page: number = 0) => {
    const response = await tableSettings({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.failedToLoadTableSettings }),
      });
    }

    dispatch(tableSettingsSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchTableSettings(++page);
    } else {
      dispatch(tableSettingsSlice.actions.setLoading(false));
    }
  };

  const [orderFlows] = useRequest(OrderFlowRequest.list);
  const fetchOrderFlows = async (page: number = 0) => {
    const response = await orderFlows({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.orderFlowsUnableToLoadError }),
      });
    }

    dispatch(orderFlowSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchOrderFlows(++page);
    } else {
      dispatch(orderFlowSlice.actions.setLoading(false));
    }
  };

  const [orderStatuses] = useRequest(OrderStatusRequest.list);
  const fetchOrderStatuses = async (page: number = 0) => {
    const response = await orderStatuses({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.orderStatusesUnableToLoadError }),
      });
    }

    dispatch(orderStatusSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchOrderStatuses(++page);
    } else {
      dispatch(orderStatusSlice.actions.setLoading(false));
    }
  };

  const [productTransactionStatuses] = useRequest(ProductTransactionStatusRequest.list);
  const fetchProductTransactionStatuses = async (page: number = 0) => {
    const response = await productTransactionStatuses({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.productTransactionStatusesUnableToLoadError }),
      });
    }

    dispatch(productTransactionStatusSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchProductTransactionStatuses(++page);
    } else {
      dispatch(productTransactionStatusSlice.actions.setLoading(false));
    }
  };

  const [productStatuses] = useRequest(ProductStatusRequest.list);
  const fetchProductStatuses = async (page: number = 0) => {
    const response = await productStatuses({ page });

    if (!response) {
      return showToast({
        type: ToastType.error,
        title: translate({ key: TranslationKey.productStatusesUnableToLoadError }),
      });
    }

    dispatch(productStatusSlice.actions.add(response.data));
    if (response.hasNext === true) {
      await fetchProductStatuses(++page);
    } else {
      dispatch(productStatusSlice.actions.setLoading(false));
    }
  };

  /**
   * The following section contains all the "old" fetchers
   * that use Apollo's useLazyQuery for paginated data loading.
   *
   * These fetchers are responsible for retrieving various
   * company-related resources from the backend, such as:
   *   - Stock location roles
   *   - Bin statuses
   *   - Files
   *   - Alerts
   *   - Custom fields
   *   - Triggers
   *   - Task export configurations
   *   - Order export configurations
   *   - Order table settings
   *   - Asset table settings
   *   - Scan rules
   *   - Comments
   *   - Billing information
   *   - Tasks
   *   - Integration settings
   *   - CSV upload configurations
   *   - Task settings
   *   - Authentication settings
   *   - Product transaction export configurations
   *   - Product transaction table settings
   *   - Reorder rules
   *   - Reorder user rules
   *   - Tags
   *   - Tag relations
   *   - Container types
   *   - Assets
   *   - Asset transactions
   *   - Reorder rule export configurations
   *   - Handling units
   *   - Product master data table settings
   *   - Product table settings
   *   - Kit templates
   *   - Kits
   *
   * Each fetcher handles pagination and dispatches the
   * corresponding Redux actions to update the application state.
   *
   * This section is marked as "OLD" to distinguish it from
   * newer data loading approaches that may use different
   * patterns or hooks.
   */
  const [fetchStockLocationRoles] = useLazyQuery<
    GetStockLocationRoleAssignmentsResponse,
    GetStockLocationRoleAssignmentsVariables
  >(StockLocationRoleAssignmentQueries.get, {
    onCompleted: async res => {
      dispatch(stockLocationRoleAssignmentSlice.actions.addAll(res.stockLocationRolesForCompany.data));
      if (res.stockLocationRolesForCompany.hasNext === true) {
        await fetchStockLocationRoles({
          variables: { companyId: currentCompany.id, page: ++res.stockLocationRolesForCompany.page },
        });
      } else {
        dispatch(stockLocationRoleAssignmentSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchBinStatuses] = useLazyQuery<GetBinStatusesResponse, GetBinStatusesVariables>(BinStatusQueries.get, {
    onCompleted: async res => {
      dispatch(binStatusSlice.actions.add(res.binStatuses.data));
      if (res.binStatuses.hasNext === true) {
        await fetchBinStatuses({ variables: { companyId: currentCompany.id, page: ++res.binStatuses.page } });
      } else {
        dispatch(binStatusSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchFiles] = useLazyQuery<GetFilesResponse, GetFilesVariables>(FileQueries.get, {
    variables: { companyId: currentCompany.id, page: 0 },
    onError: () => dispatch(fileSlice.actions.setLoading(false)),
    onCompleted: async response => {
      dispatch(fileSlice.actions.addAll(response.files.data));
      if (response.files.hasNext === true) {
        await fetchFiles({ variables: { companyId: currentCompany.id, page: ++response.files.page } });
      } else {
        dispatch(fileSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchAlerts] = useLazyQuery<GetAlertResponse, GetAlertVariables>(AlertQueries.get, {
    onCompleted: async res => {
      dispatch(alertSlice.actions.add(res.alerts.data));

      if (res.alerts.hasNext === true) {
        fetchAlerts({
          variables: {
            page: ++res.alerts.page,
          },
        });
      } else {
        dispatch(alertSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchCustomFields] = useLazyQuery<GetCustomFieldResponse, GetCustomFieldVariables>(CustomFieldQueries.get, {
    variables: { companyId: currentCompany.companyId },
    onCompleted: async res => {
      dispatch(customFieldSlice.actions.addAll(res.customFields.data));
      if (res.customFields.hasNext === true) {
        fetchCustomFields({
          variables: {
            companyId: currentCompany.companyId,
            page: ++res.customFields.page,
          },
        });
      } else {
        dispatch(customFieldSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchTriggers] = useLazyQuery<GetTriggerResponse, GetTriggerVariables>(TriggerQueries.get, {
    onCompleted: async res => {
      dispatch(triggerSlice.actions.add(res.triggers.data));

      if (res.triggers.hasNext === true) {
        fetchTriggers({
          variables: {
            page: ++res.triggers.page,
          },
        });
      } else {
        dispatch(triggerSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchOrderExportConfigurations] = useLazyQuery<
    GetOrderExportConfigurationsResponse,
    GetOrderExportConfigurationsVariables
  >(OrderExportConfigurationQueries.get, {
    variables: { companyId: currentCompany.companyId },
    onCompleted: async res => {
      dispatch(orderExportConfigurationSlice.actions.addAll(res.orderExportConfigurations.data));

      if (res.orderExportConfigurations.hasNext === true) {
        fetchOrderExportConfigurations({
          variables: {
            companyId: currentCompany.companyId,
            page: ++res.orderExportConfigurations.page,
          },
        });
      } else {
        dispatch(orderExportConfigurationSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchScanRules] = useLazyQuery<GetScanRuleResponse, GetScanRuleVariables>(ScanRuleQueries.get, {
    variables: {},
    onCompleted: async res => {
      dispatch(scanRuleSlice.actions.addAll(res.scanRules.data));
      if (res.scanRules.hasNext === true) {
        fetchScanRules({ variables: { page: ++res.scanRules.page } });
      } else {
        dispatch(scanRuleSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchComments] = useLazyQuery<GetCommentsResponse, GetCommentsVariables>(CommentQueries.get, {
    variables: { companyId: currentCompany.companyId },
    onCompleted: async res => {
      dispatch(commentSlice.actions.add(res.comments.data));
      dispatch(commentSlice.actions.setLoading(false));
    },
  });

  const [fetchTasks] = useLazyQuery<GetTasksResponse, GetTasksVariables>(TaskQueries.get, {
    onCompleted: async res => {
      dispatch(taskSlice.actions.add(res.tasks.data));
      if (res.tasks.hasNext === true) {
        fetchTasks({ variables: { page: ++res.tasks.page } });
      } else {
        dispatch(taskSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchIntegrations] = useLazyQuery<GetIntegrationSettingsResponse, GetIntegrationSettingsVariables>(
    IntegrationSettingsQueries.get,
    {
      variables: { companyId: currentCompany.companyId },
      onCompleted: async res => {
        dispatch(integrationSettingsSlice.actions.add(res.integrationSettings));
        dispatch(integrationSettingsSlice.actions.setLoading(false));
      },
    },
  );

  const [fetchCsvUploadConfigurations] = useLazyQuery<
    GetCsvUploadConfigurationsResponse,
    GetCsvUploadConfigurationsVariables
  >(CsvUploadConfigurationQueries.get, {
    variables: { companyId: currentCompany.id, page: 0 },
    onCompleted: async response => {
      dispatch(csvUploadConfigurationSlice.actions.addAll(response.csvUploadConfigurations.data));
      if (response.csvUploadConfigurations.hasNext === true) {
        await fetchCsvUploadConfigurations({
          variables: {
            companyId: currentCompany.id,
            page: ++response.csvUploadConfigurations.page,
          },
        });
      } else {
        dispatch(csvUploadConfigurationSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchTaskSettings] = useLazyQuery<GetTaskSettingsResponse, GetTaskSettingsVariables>(TaskSettingsQueries.get, {
    variables: { companyId: currentCompany.companyId, page: 0 },
    onCompleted: async response => {
      dispatch(taskSettingsSlice.actions.addAll(response.taskSettings.data));
      if (response.taskSettings.hasNext === true) {
        await fetchTaskSettings({ variables: { companyId: currentCompany.id, page: ++response.taskSettings.page } });
      } else {
        dispatch(taskSettingsSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchAuthenticationSettings] = useLazyQuery<
    GetAuthenticationSettingsResponse,
    GetAuthenticationSettingsVariables
  >(AuthenticationSettingsQueries.get, {
    variables: { companyId: currentCompany.companyId },
    onCompleted: async response => {
      dispatch(authenticationSettingsSlice.actions.set(response.authenticationSettings.data));
      dispatch(authenticationSettingsSlice.actions.setLoading(false));
    },
  });

  const [fetchReorderRules] = useLazyQuery<GetReorderRulesResponse, GetReorderRulesVariables>(ReorderRuleQueries.get, {
    variables: { companyId: currentCompany.id },
    onCompleted: async response => {
      dispatch(reorderRuleSlice.actions.addAll(response.reorderRules.data));
      if (response.reorderRules.hasNext === true) {
        await fetchReorderRules({ variables: { companyId: currentCompany.id, page: ++response.reorderRules.page } });
      } else {
        dispatch(reorderRuleSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchReorderUserRules] = useLazyQuery<GetReorderUserRulesResponse, GetReorderUserRulesVariables>(
    ReorderUserRuleQueries.get,
    {
      variables: { companyId: currentCompany.id },
      onCompleted: async response => {
        dispatch(reorderUserRuleSlice.actions.addAll(response.reorderUserRules.data));
        if (response.reorderUserRules.hasNext === true) {
          await fetchReorderUserRules({
            variables: { companyId: currentCompany.id, page: ++response.reorderUserRules.page },
          });
        } else {
          dispatch(reorderUserRuleSlice.actions.setLoading(false));
        }
      },
    },
  );

  const [fetchTags] = useLazyQuery<GetTagsResponse, GetTagsVariables>(TagQueries.get, {
    onCompleted: async response => {
      dispatch(tagSlice.actions.addAll(response.tags.data));
      if (response.tags.hasNext === true) {
        await fetchTags({ variables: { page: ++response.tags.page } });
      } else {
        dispatch(tagSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchTagRelations] = useLazyQuery<GetTagRelationsResponse, GetTagRelationsVariables>(TagRelationQueries.get, {
    onCompleted: async response => {
      dispatch(tagRelationSlice.actions.addAll(response.tagRelations.data));
      if (response.tagRelations.hasNext === true) {
        await fetchTagRelations({ variables: { page: ++response.tagRelations.page } });
      } else {
        dispatch(tagRelationSlice.actions.setLoading(false));
      }
    },
  });

  const [loadAllProductTransactions] = useLazyQuery<GetProductTransactionsResponse, GetProductTransactionsVariables>(
    ProductTransactionQueries.get,
    {
      variables: { companyId: currentCompany.companyId, since: new Date(0) },
      onCompleted: async res => {
        dispatch(productTransactionSlice.actions.add(res.productTransactions.data));
        if (res.productTransactions.hasNext === true) {
          await loadAllProductTransactions({
            variables: {
              companyId: currentCompany.companyId,
              page: ++res.productTransactions.page,
              since: new Date(0),
            },
          });
        }
      },
    },
  );

  const [fetchContainerTypes] = useLazyQuery<GetContainerTypesResponse, GetContainerTypesVariables>(
    ContainerTypeQueries.get,
    {
      variables: { companyId: currentCompany.id },
      onCompleted: async response => {
        dispatch(containerTypeSlice.actions.addAll(response.containerTypes.data));

        if (response.containerTypes.hasNext === true) {
          await fetchContainerTypes({
            variables: { companyId: currentCompany.id, page: ++response.containerTypes.page },
          });
        } else {
          dispatch(containerTypeSlice.actions.setLoading(false));
        }
      },
    },
  );

  const [fetchHandlingUnits] = useLazyQuery<GetHandlingUnitsResponse, GetHandlingUnitsVariables>(
    HandlingUnitQueries.get,
    {
      onCompleted: async response => {
        dispatch(handlingUnitSlice.actions.add(response.handlingUnits.data));
        if (response.handlingUnits.hasNext) {
          await fetchHandlingUnits({ variables: { page: ++response.handlingUnits.page } });
        } else {
          dispatch(handlingUnitSlice.actions.setLoading(false));
        }
      },
    },
  );

  const [fetchKitTemplates] = useLazyQuery<GetKitTemplatesResponse, GetKitTemplatesVariables>(KitTemplateQueries.get, {
    onCompleted: async response => {
      dispatch(kitTemplateSlice.actions.add(response.kitTemplates.data));
      if (response.kitTemplates.hasNext === true) {
        await fetchKitTemplates({ variables: { page: ++response.kitTemplates.page } });
      } else {
        dispatch(kitTemplateSlice.actions.setLoading(false));
      }
    },
  });

  const [fetchKits] = useLazyQuery<GetKitsResponse, GetKitsVariables>(KitQueries.get, {
    onCompleted: async response => {
      dispatch(kitSlice.actions.add(response.kits.data));
      if (response.kits.hasNext === true) {
        await fetchKits({ variables: { page: ++response.kits.page } });
      } else {
        dispatch(kitSlice.actions.setLoading(false));
      }
    },
  });

  function resetCompany() {
    dispatch(alertSlice.actions.reset());
    dispatch(assetSlice.actions.reset());
    dispatch(assetExportConfigurationSlice.actions.reset());
    dispatch(assetTransactionSlice.actions.reset());
    dispatch(authenticationSettingsSlice.actions.reset());
    dispatch(binSlice.actions.reset());
    dispatch(binStatusSlice.actions.reset());
    dispatch(commentSlice.actions.reset());
    dispatch(contactSlice.actions.reset());
    dispatch(containerSlice.actions.reset());
    dispatch(containerTypeSlice.actions.reset());
    dispatch(csvUploadConfigurationSlice.actions.reset());
    dispatch(customFieldSlice.actions.reset());
    dispatch(dashboardSlice.actions.reset());
    dispatch(fileSlice.actions.reset());
    dispatch(lotSlice.actions.reset());
    dispatch(orderExportConfigurationSlice.actions.reset());
    dispatch(orderSlice.actions.reset());
    dispatch(productExportConfigurationSlice.actions.reset());
    dispatch(productMasterDataExportConfigurationSlice.actions.reset());
    dispatch(productMasterDataSlice.actions.reset());
    dispatch(productSlice.actions.reset());
    dispatch(productTransactionExportConfigurationSlice.actions.reset());
    dispatch(productTransactionSlice.actions.reset());
    dispatch(reorderRuleSlice.actions.reset());
    dispatch(reorderUserRuleSlice.actions.reset());
    dispatch(scanRuleSlice.actions.reset());
    dispatch(stockLocationRoleAssignmentSlice.actions.reset());
    dispatch(stockLocationSlice.actions.reset());
    dispatch(tagRelationSlice.actions.reset());
    dispatch(tagSlice.actions.reset());
    dispatch(taskExportConfigurationSlice.actions.reset());
    dispatch(taskSettingsSlice.actions.reset());
    dispatch(taskSlice.actions.reset());
    dispatch(triggerSlice.actions.reset());
    dispatch(reorderRuleExportConfigurationSlice.actions.reset());
    dispatch(handlingUnitSlice.actions.reset());
    dispatch(kitTemplateSlice.actions.reset());
    dispatch(kitSlice.actions.reset());
    dispatch(barcodeExportConfigurationSlice.actions.reset());
    dispatch(binConstraintSlice.actions.reset());
    dispatch(pdfTemplateSlice.actions.reset());
    dispatch(tableSettingsSlice.actions.reset());
  }

  function loadCompany(company: Company) {
    currentCompany = company;

    fetchStockLocations();
    fetchProductMasterData();
    fetchBins();

    fetchProductTransactions();
    fetchProductTransactionExportConfigurations();

    fetchOrders();
    fetchOrderFlows();
    fetchOrderStatuses();
    fetchProductTransactionStatuses();
    fetchProductStatuses();

    fetchLots();
    fetchContainers();
    fetchProducts();
    fetchContacts();

    fetchProductExportConfigurations();
    fetchTaskExportConfigurations();
    fetchProductMasterDataExportConfigurations();

    fetchBarcodeExportConfigurations();
    fetchPdfTemplates();

    fetchBinConstraints();

    fetchAssets();
    fetchAssetExportConfigurations();
    fetchAssetTransactions();
    fetchTableSettings();

    // OLD
    fetchStockLocationRoles({ variables: { companyId: currentCompany.id, page: 0 } });
    fetchBinStatuses({ variables: { companyId: currentCompany.id, page: 0 } });
    fetchFiles({ variables: { companyId: currentCompany.id, page: 0 } });
    fetchAlerts({ variables: { page: 0 } });
    fetchCustomFields({ variables: { companyId: currentCompany.id, page: 0 } });
    fetchTriggers({ variables: { page: 0 } });
    fetchOrderExportConfigurations({ variables: { companyId: currentCompany.id, page: 0 } });
    // fetchScanRules({ variables: { companyId: currentCompany.id, page: 0 } });
    fetchComments({ variables: { companyId: currentCompany.id } });
    fetchTasks({ variables: { page: 0 } });
    fetchCsvUploadConfigurations({ variables: { companyId: currentCompany.id, page: 0 } });
    fetchTaskSettings({ variables: { companyId: currentCompany.id, page: 0 } });
    fetchAuthenticationSettings({ variables: { companyId: currentCompany.id } });
    fetchReorderRules({ variables: { companyId: currentCompany.id, page: 0 } });
    fetchReorderUserRules({ variables: { companyId: currentCompany.id, page: 0 } });
    fetchTags({ variables: { page: 0 } });
    fetchTagRelations({ variables: { page: 0 } });
    fetchContainerTypes({ variables: { companyId: currentCompany.id, page: 0 } });

    fetchIntegrations({ variables: { companyId: currentCompany.id } });
    fetchReorderRuleExportConfigurations();
    fetchHandlingUnits({ variables: { page: 0 } });
    fetchKitTemplates({ variables: { page: 0 } });
    fetchKits({ variables: { page: 0 } });
  }

  function reloadCompany(company: Company) {
    console.info('[RELOAD] Company');
    resetCompany();
    loadCompany(company);
  }

  return { fetchFiles, reloadCompany };
}
